import { Aurora, AuroraAssetGroup } from '../types'
import { useState } from 'react'
import { setAuroraCirculatorPumpStatus } from '../services/aurora'
import { AuroraCirculationPumpStatus } from '../enums'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import AuroraCirculationPumpStatusControl from './AuroraCirculationPumpStatusControl'

export interface AuroraAssetGroupCirculationPumpStatusControlProps {
  auroraAssetGroup: AuroraAssetGroup
  beforeSubmit?: (status: AuroraCirculationPumpStatus) => void
  afterSubmit?: (status: AuroraCirculationPumpStatus) => void
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupCirculationPumpStatusControl({
  auroraAssetGroup,
  beforeSubmit,
  afterSubmit,
  onError
}: AuroraAssetGroupCirculationPumpStatusControlProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The auroras.
   */
  const auroras = auroraAssetGroup.assets
    .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
    .map(({ aurora, ...asset }) => ({ ...aurora, asset }) as Aurora)

  /**
   * The circulation pump status.
   */
  const [status, setStatus] = useState<AuroraCirculationPumpStatus>(
    auroras.every((aurora) => aurora.isCirculationPumpOn)
      ? AuroraCirculationPumpStatus.ON
      : AuroraCirculationPumpStatus.OFF
  )

  /**
   * Indicates if the submit is in progress.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * Handle the submit event.
   */
  async function handleSubmit(status: AuroraCirculationPumpStatus) {
    try {
      setSubmitting(true)

      beforeSubmit?.(status)

      await Promise.all(auroras.map((aurora) => setAuroraCirculatorPumpStatus(aurora.id, status)))
      setStatus(status)
      dispatch(createSuccessNotification(t('setCirculationPumpStatusSuccess')))

      afterSubmit?.(status)
    } catch (error) {
      onError?.(error)
      dispatch(createErrorNotification(t('setCirculationPumpStatusError')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuroraCirculationPumpStatusControl
      value={status}
      loading={isSubmitting}
      onSubmit={handleSubmit}
    />
  )
}
